import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, X, Loader2, CheckCircle2, Clock, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { assistAPI, myVehiclesAPI } from '../services/api';

const PhotoDiagnosticModal = ({ product, onClose }) => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [quota, setQuota] = useState(null);
  const loadQuota = () => assistAPI.diagnosticsQuota().then(setQuota).catch(() => {});

  useEffect(() => {
    if (!user) return;
    myVehiclesAPI.list().then((v) => { setVehicles(v); if (v[0]) setVehicleId(v[0].id); }).catch(() => {});
    loadQuota();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(URL.createObjectURL(f)); setResult(null); setError('');
  };
  const submit = async () => {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('product_id', product?.id || ''); fd.append('vehicle_id', vehicleId); fd.append('note', note);
      setResult(await assistAPI.createDiagnostic(fd));
      loadQuota();
    } catch (e) { setError(e.response?.data?.detail || 'Envoi impossible'); loadQuota(); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="photo-diagnostic-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Camera size={18} className="text-orange-500" /> Vérifier ma pièce par photo</h3>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" data-testid="photo-diagnostic-close"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          {!user ? (
            <p className="text-sm text-gray-700">Connectez-vous pour envoyer une photo et recevoir la référence confirmée. <Link to="/connexion" className="text-orange-600 underline" data-testid="photo-diagnostic-login">Se connecter</Link></p>
          ) : result ? (
            <div className={`rounded-lg p-4 text-sm ${result.status === 'confirmed' ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`} data-testid="photo-diagnostic-result">
              {result.status === 'confirmed' ? (
                <>
                  <p className="font-semibold flex items-center gap-1"><CheckCircle2 size={16} /> Référence confirmée : <span className="font-mono">{result.final_reference}</span> ({Math.round(result.confidence)} % de confiance)</p>
                  <p className="mt-1">{result.ai?.explanation}</p>
                  {result.final_product_slug && <Link to={`/produit/${result.final_product_slug}`} className="inline-block mt-3 bg-orange-500 text-white px-4 py-2 rounded-lg" data-testid="photo-diagnostic-product-link">Voir la pièce confirmée</Link>}
                </>
              ) : (
                <>
                  <p className="font-semibold flex items-center gap-1"><Clock size={16} /> Photo transmise à notre équipe</p>
                  <p className="mt-1">{result.ai?.explanation || 'L\'analyse automatique n\'est pas assez sûre'} {result.ai_reference ? `(piste : ${result.ai_reference})` : ''}. Un expert vérifie votre photo : vous recevrez la référence confirmée par e-mail et dans votre espace client.</p>
                </>
              )}
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">Photographiez la pièce usée, sa plaque constructeur, votre numéro moteur ou votre plaque d'immatriculation : notre IA compare avec le catalogue compatible et un expert valide si nécessaire.</p>
              {quota && !quota.unlimited && (
                <div className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-xs ${quota.remaining === 0 ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-600'}`} data-testid="photo-diagnostic-quota">
                  <span><strong data-testid="photo-diagnostic-quota-remaining">{quota.remaining}/{quota.limit}</strong> {quota.remaining > 1 ? 'analyses restantes' : 'analyse restante'} aujourd'hui{quota.remaining === 0 ? ' — réessayez demain' : ''}</span>
                  <span className="flex gap-1" aria-hidden="true">{Array.from({ length: quota.limit }).map((_, i) => <span key={i} className={`h-1.5 w-4 rounded-full ${i < quota.remaining ? 'bg-orange-500' : 'bg-gray-300'}`} />)}</span>
                </div>
              )}
              {vehicles.length > 0 ? (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Votre véhicule</label>
                  <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" data-testid="photo-diagnostic-vehicle">
                    {vehicles.map((v) => <option key={v.id} value={v.id}>{v.brand} {v.model} {v.year}{v.engine_name ? ` — ${v.engine_name}` : ''}</option>)}
                  </select>
                </div>
              ) : <p className="text-xs text-gray-500">Astuce : enregistrez votre véhicule dans <Link to="/mon-compte/garage" className="underline">Mon garage</Link> pour une analyse plus précise.</p>}
              <label className="block border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-orange-400" data-testid="photo-diagnostic-dropzone">
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="photo-diagnostic-file" />
                {preview ? <img src={preview} alt="" className="max-h-48 mx-auto rounded" /> : <span className="text-sm text-gray-600 inline-flex items-center gap-2"><Upload size={16} /> Choisir ou prendre une photo (JPEG, PNG, WEBP – 8 Mo max)</span>}
              </label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Précisions (optionnel) : côté, symptôme, référence lue…" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" data-testid="photo-diagnostic-note" />
              {error && <p className="text-sm text-red-600" data-testid="photo-diagnostic-error">{error}</p>}
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose}>Fermer</Button>
          {user && !result && <Button onClick={submit} disabled={busy || !file || quota?.remaining === 0} className="bg-orange-500 hover:bg-orange-600" data-testid="photo-diagnostic-submit">{busy ? <><Loader2 size={16} className="mr-1 animate-spin" /> Analyse en cours…</> : 'Analyser ma photo'}</Button>}
        </div>
      </div>
    </div>
  );
};

export default PhotoDiagnosticModal;
